import { Dispatch, SetStateAction } from 'react';
import { FormValues } from '@/components/pages/Users/Index/UserListForm/Container';
import i18n from '@/settings/i18n';
import { userListApi } from '@/services/api/userList';
import { User } from '@/types/userList';

import * as Sentry from '@sentry/react';
import axios from 'axios';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';

type Props = {
  organizationId: number;
  selectedUserId: number | null;
  setIsUpdating: Dispatch<SetStateAction<boolean>>;
  setSelectedUserId: Dispatch<SetStateAction<number | null>>;
  setUsers: Dispatch<SetStateAction<User[] | null>>;
};

export const useUpdateUser = ({
  organizationId,
  selectedUserId,
  setIsUpdating,
  setSelectedUserId,
  setUsers,
}: Props) => {
  const { apiErrorHandler } = useApiErrorHandler();
  const { flashNotice } = useFlashMessenger();
  const updateUser = async (values: FormValues) => {
    if (!selectedUserId) {
      return;
    }

    try {
      setIsUpdating(true);

      await userListApi.updateUser(organizationId, selectedUserId, values);

      const {
        data: { users },
      } = await userListApi.getUsers(organizationId);

      setUsers(users);
      setSelectedUserId(null);

      flashNotice(i18n.t('text.user.update.done'));
    } catch (err) {
      if (
        axios.isAxiosError(err) &&
        err.response &&
        err.response.status === 401
      ) {
        try {
          localStorage.clear();
        } catch (e) {
          Sentry.captureException(e);
        }
      }
      apiErrorHandler(err);
    } finally {
      setIsUpdating(false);
    }
  };

  return { updateUser };
};
